import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import Intercom from '@intercom/messenger-js-sdk';
import { environment } from '../../environments/environment';
import { IntercomConfig } from '../common/models/intercom-config';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import {
  HeaderParams,
  MainComponent,
  SociumApp,
  StatesService,
  StateStoreKeys,
  UserRole,
  UserRolesPermissionsService,
  Company as FlowCompany,
} from '@socium/sociumflow';
import { AuthService } from '../features/auth/auth.service';
import { AsideParams } from '@socium/sociumflow/lib/components/layouts/aside/aside.params';
import { TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { LanguageCode } from 'libs/src';
import { Company } from '../features/user/models/company.model';
import { Company as _company } from '@socium/common-api';
import { LanguageService } from '../features/translations/services/language.service';
import { RouterHelper } from '../common/helpers/routes-helper';
import { User } from '../features/user/user.model';
import { AppMenuItem } from './utils/sidebar-model';
import { adminMenu, employeeMenu, sociumAdminMenu } from './utils/sidebar-menu';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Applications } from '@socium/common-api';
import { take } from 'rxjs';

/**
 * Represents the main layout structure of the application, including the toolbar, sidebar, and main content area.
 */
@Component({
  selector: 'contract-v2-app-layout',
  imports: [
    RouterOutlet,
    TooltipModule,
    NgxSpinnerModule,
    ButtonModule,
    MainComponent,
  ],
  template: `
    <socium-main-layout
      [companyId]="selectedCompanyId"
      [asideParams]="asideParams"
      [headerParams]="headerParams"
      [appsParams]="appsParams"
      (companyChange)="handleSelectCompany($event)"
      (languageChange)="onLanguageChange($event)"
      (myProfile)="handleNavigateToUserProfile()"
      (loggedOut)="logout()"
    >
      <ngx-spinner
        size="medium"
        color="#0573e880"
        type="ball-clip-rotate"
        bdColor="rgba(233,236,239,0.7)"
        [fullScreen]="true"
      />

      <router-outlet></router-outlet>
    </socium-main-layout>
  `,
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LayoutComponent implements OnInit {
  private readonly statesService = inject(StatesService);
  private readonly userRolesPermissionsService = inject(
    UserRolesPermissionsService,
  );
  private readonly authService = inject(AuthService);
  private user = this.authService.getAuthenticatedUser();
  public readonly isEmployee = this.authService.isEmployee();
  private company = this.authService.getCompany() as Company;
  private readonly translate = inject(TranslateService);
  private readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly spinner = inject(NgxSpinnerService);
  public companies: Company[] = [];
  public selectedCompanyId = '';

  appsParams = {
    displayApps: true,
    activeApp: SociumApp.DOC,
  };

  headerParams: HeaderParams = {
    application: Applications.DOC,
    companies: [],
    selectedCompany: {
      id: this.company._id as string,
      name: this.company.name,
      logo: this.company.logo || null,
      companyHolding:
        (typeof this.company.companyHolding === 'string'
          ? (this.company.companyHolding as string)
          : (this.company.companyHolding as Company)?._id) || this.company?._id,
    },
    user: {
      firstName: this.user?.firstName as string,
      lastName: this.user?.lastName as string,
      id: this.user?._id as string,
      isAdmin: null,
      avatar: this.user?.avatar || null,
      role: this.user?.role,
    },
    displaySearch: false,
    displayDropdown: true,
    hasNotifications: false,
    isLoading: false,
  };

  asideParams: AsideParams = {
    app: SociumApp.DOC,
    groups: [],
    isCollapsed: false,
  };

  ngOnInit(): void {
    this.hydrateSelectedCompany();
    this.handleFnCallByRole(this.user as User);
    this.intializeMenu();
  }

  constructor() {
    this.hydrateCompanies();
    const user = this.statesService.getCurrentUser() as unknown as User | null;
    const { lastName, firstName, email, company, role, hash } = user ?? {};
    if (hash && (environment.showIntercom || environment.production))
      this.intercomConfig({
        email: email as string,
        fullName: `${firstName} ${lastName}`,
        companyId: company?.id as string,
        companyName: company?.name as string,
        userRole: role as unknown as UserRole,
        hash: hash as string,
      });
  }

  handleFnCallByRole(user: User) {
    switch (true) {
      case this.userRolesPermissionsService.hasSociumManagementAccess():
        this.headerParams.fromNative = true;
        this.headerParams.user.isAdmin = true;
        this.headerParams.displayDropdown = true;
        break;
      case user.role && user.role === UserRole.HOLDING_ADMIN:
        this.headerParams.fromNative = true;
        this.headerParams.user.isAdmin = true;
        this.headerParams.displayDropdown = true;
        break;
      case user.role && user.role === UserRole.HR_SUPER_ADMIN:
        this.headerParams.fromNative = false;
        break;
      default:
        this.headerParams.fromNative = false;
        break;
    }
  }

  onNavigation(path: string) {
    this.router.navigate([path]);
  }

  intercomConfig(config: IntercomConfig) {
    Intercom({
      app_id: environment.intercomAppId,
      created_at: new Date().getTime(),
      email: config.email,
      user_hash: config.hash,
      name: config.fullName,
      company: {
        id: config.companyId,
        name: config.companyName,
      },
      custom_attributes: {
        role: config.userRole,
      },
    });
  }

  intializeMenu() {
    const items = this.getMenuAccordingRoles();
    this.asideParams.groups = [
      {
        label: 'Navigation',
        isClosed: false,
        items: items[0].items.map((elt) => ({
          label: this.translate.instant(elt.label),
          iconClass: elt.icon,
          redirectUrl: elt.routerLink[0],
          isEnabled: true,
        })),
      },
    ];
  }

  private getMenuAccordingRoles(): AppMenuItem[] {
    if (this.authService.isSociumAdmin()) return sociumAdminMenu;

    if (this.authService.isCompanyAdmin() || this.authService.isHoldingAdmin())
      return adminMenu;

    if (this.authService.isEmployee()) return employeeMenu;

    return [];
  }

  hydrateCompanies() {
    if (!this.authService.isSwitchCompanyDropdownVisible()) {
      this.headerParams = { ...this.headerParams, displayDropdown: false };
      return;
    }
    this.authService.getCompaniesThatHaveDocEnabled().subscribe({
      next: (res) => {
        this.companies = res;
        this.headerParams = {
          ...this.headerParams,
          companies: res.map((elt) => ({
            id: elt._id as string,
            name: elt.name as string,
            logo: elt.logo,
          })),
        };
      },
    });
  }

  private hydrateSelectedCompany() {
    this.statesService
      .getSelectedCompany$()
      .pipe(take(1))
      .subscribe((companyLib) => {
        const company = companyLib as FlowCompany;
        this.company = company as unknown as Company;
        if (company) {
          this.selectedCompanyId = company._id || company.id || '';
          const isCompanyHoldingString =
            typeof company.companyHolding === 'string';
          this.headerParams = {
            ...this.headerParams,
            selectedCompany: {
              id: company.id || '',
              name: company.name ?? '',
              logo: company.logo,
              companyHolding:
                (isCompanyHoldingString
                  ? (company.companyHolding as string)
                  : (company.companyHolding as unknown as Company)?._id) ||
                company?._id,
            },
          };
        }
      });
  }

  handleSelectCompany(company: _company) {
    const selectedCompanyId = company?.id || company?._id || '';
    if (selectedCompanyId === this.selectedCompanyId) return;
    this.statesService.setCookie<string>(
      StateStoreKeys.SELECTED_COMPANY_ID,
      selectedCompanyId,
    );
    this.statesService.setSelectedCompany(company as unknown as FlowCompany);
    this.selectedCompanyId = selectedCompanyId;
    this.intializeMenu();
    this.router.navigate(['/document-requests']).finally(() => {
      location.reload();
    });
  }

  public handleNavigateToUserProfile(): void {
    this.isEmployee
      ? RouterHelper.navigateToProfilePage(this.router)
      : RouterHelper.navigateToEmployeeDetailPage(
          this.router,
          this.user as User,
        );
  }

  public logout(): void {
    this.spinner.show();
    this.authService.logout().then(() => this.spinner.hide());
  }

  public onLanguageChange(lang: string) {
    const selectedLang =
      lang == LanguageCode.FR.toUpperCase() ? LanguageCode.FR : LanguageCode.EN;
    this.translate.use(selectedLang);
    this.language.saveChangeLang(selectedLang.toLowerCase());
    location.reload();
  }
}
